import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { api, formatApiError, formatINR, useSEO } from "@/lib/api";

const STATUS_COLORS = {
  paid: "bg-green-100 text-green-800",
  cod_pending: "bg-amber-100 text-amber-800",
  pending: "bg-gray-100 text-gray-800",
  failed: "bg-red-100 text-red-800",
};

const STATUSES = ["confirmed", "processing", "shipped", "delivered", "cancelled"];

export default function AdminOrders() {
  useSEO("Admin — Orders");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoading(true);
    const url = filter === "all" ? "/admin/orders" : `/admin/orders?status=${filter}`;
    api.get(url).then((r) => setOrders(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const saveField = async (order_number, field, value) => {
    try {
      await api.put(`/admin/orders/${order_number}`, { [field]: value });
      toast.success("Updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const total = orders.reduce((sum, o) => sum + (o.amount || 0), 0);

  return (
    <div data-testid="admin-orders-page" className="pt-16 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <Link to="/account" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-black">
              <ArrowLeft size={14} /> Account
            </Link>
            <h1 className="font-display text-4xl md:text-6xl tracking-[-0.03em] leading-none mt-3">Orders</h1>
            <p className="text-sm text-muted-foreground mt-2">{orders.length} orders · {formatINR(total)} total</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-2 rounded-full border border-black/20 px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-black hover:text-white transition-colors">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "paid", "cod_pending", "pending", "failed"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${filter === s ? "bg-black text-white border-black" : "border-black/15 hover:border-black"}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-muted-foreground py-8">Loading…</p>}

        {!loading && orders.length === 0 && (
          <p className="text-sm text-muted-foreground py-16 text-center">No orders yet.</p>
        )}

        <div className="space-y-3" data-testid="orders-list">
          {orders.map((o) => (
            <div key={o.order_number} data-testid={`order-row-${o.order_number}`}
              className="rounded-2xl bg-white border border-black/10 p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Order</p>
                  <p className="font-display text-2xl">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(o.created_at).toLocaleString()} · {(o.gateway || "").toUpperCase()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-display text-xl" data-testid={`order-total-${o.order_number}`}>{formatINR(o.amount)}</p>
                  <span className={`inline-block mt-1 text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${STATUS_COLORS[o.payment_status] || STATUS_COLORS.pending}`}>
                    {(o.payment_status || "pending").replace("_", " ")}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 pt-5 border-t border-black/5">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Customer</p>
                  <p className="text-sm" data-testid={`order-customer-${o.order_number}`}>{o.customer?.name || "—"}</p>
                  <p className="text-xs text-muted-foreground">{o.customer?.email || "—"}</p>
                  <p className="text-xs text-muted-foreground">{o.customer?.phone || "—"}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Shipping</p>
                  {o.customer?.address_line1 ? (
                    <div className="text-xs text-muted-foreground leading-relaxed">
                      <p className="text-black">{o.customer.address_line1}</p>
                      {o.customer.address_line2 && <p>{o.customer.address_line2}</p>}
                      <p>{[o.customer.city, o.customer.district].filter(Boolean).join(", ")}</p>
                      <p>{[o.customer.state, o.customer.pincode].filter(Boolean).join(" — ")}</p>
                      <p>{o.customer.country || "India"}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground whitespace-pre-wrap">{o.customer?.address || "—"}</p>
                  )}
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Items</p>
                  <div className="space-y-1.5">
                    {(o.items || []).map((it, i) => {
                      const unit = it.price || 0;
                      const qty = it.quantity || 0;
                      const lineTotal = it.line_total ?? unit * qty;
                      return (
                        <div key={i} className="text-xs text-muted-foreground" data-testid={`order-item-${o.order_number}-${i}`}>
                          <p className="text-black">{it.name}{it.sku ? <span className="ml-1 text-muted-foreground">· <span data-testid={`order-item-sku-${o.order_number}-${i}`}>{it.sku}</span></span> : null}</p>
                          <p>{qty} × {formatINR(unit)} = <span className="text-black">{formatINR(lineTotal)}</span></p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <select value={o.status || "confirmed"} onChange={(e) => saveField(o.order_number, "status", e.target.value)}
                  data-testid={`order-status-${o.order_number}`}
                  className="bg-[#F5F5F5] rounded-xl px-4 py-2.5 text-xs uppercase tracking-widest outline-none">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={o.payment_status || "pending"} onChange={(e) => saveField(o.order_number, "payment_status", e.target.value)}
                  data-testid={`order-payment-${o.order_number}`}
                  className="bg-[#F5F5F5] rounded-xl px-4 py-2.5 text-xs uppercase tracking-widest outline-none">
                  {["pending", "paid", "cod_pending", "failed", "refunded"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input defaultValue={o.tracking_number || ""} placeholder="Tracking number"
                  onBlur={(e) => e.target.value !== (o.tracking_number || "") && saveField(o.order_number, "tracking_number", e.target.value)}
                  data-testid={`order-tracking-${o.order_number}`}
                  className="bg-[#F5F5F5] rounded-xl px-4 py-2.5 text-xs outline-none" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
